import type { InputHTMLAttributes, ReactNode } from 'react';

import { cx } from '../../lib/cx';

import styles from './Checkbox.module.css';

export interface ChoiceProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** What is chosen by ticking it; it is the control's name. */
  label: ReactNode;
  /** A line under the label about what the choice means. It is read with the label. */
  hint?: ReactNode;
}

function Choice({ type, label, hint, className, ...rest }: ChoiceProps & { type: 'checkbox' | 'radio' }) {
  return (
    <label className={cx(styles.choice, rest.disabled && styles.disabled, className)}>
      <input type={type} className={styles.input} {...rest} />
      <span className={styles.text}>
        <span className={styles.label}>{label}</span>
        {hint ? <span className={styles.hint}>{hint}</span> : null}
      </span>
    </label>
  );
}

/**
 * One thing that is so or is not, with its label: the browser's checkbox, drawn by
 * the kit's form styles, and the whole line to press. Several of them make a set to
 * pick any number from; a setting that takes effect at once is a Toggle.
 */
export function Checkbox(props: ChoiceProps) {
  return <Choice type="checkbox" {...props} />;
}

/** One of several that exclude each other: give those of a group the same `name`. */
export function Radio(props: ChoiceProps) {
  return <Choice type="radio" {...props} />;
}
